import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import MaskedValue from '../../components/MaskedValue';

const STATUS_VARIANTS = {
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-600',
  info: 'bg-blue-100 text-blue-600',
  neutral: 'bg-slate-100 text-slate-600',
};

const PAGE_SIZE = 10;

const Transactions = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!user?.id) return;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await api.get(`/transactions/history/${user.id}`);
        const payload = response.data?.transactions || [];
        setTransactions(payload);
        setVisibleCount(Math.min(PAGE_SIZE, payload.length));
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Não foi possível carregar o extrato.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [user?.id]);

  // Prefixo/valor/sufixo separados, e não uma string só: a máscara de saldo oculto substitui só o
  // número, mantendo "R$" ou "BTC" visível — é o que o ícone de olho promete.
  const amountBTC = (formatted) => ({ prefix: '', value: formatted || '0,00000000', suffix: ' BTC' });
  const amountBRL = (formatted) => ({ prefix: 'R$ ', value: formatted || '0,00', suffix: '' });

  const rows = useMemo(() => {
    return transactions.map((transaction) => {
      const toneClass = STATUS_VARIANTS[transaction.status?.tone] || STATUS_VARIANTS.neutral;
      const isBTC = transaction.currencyLabel === 'BTC';
      const montar = isBTC ? amountBTC : amountBRL;

      return {
        id: transaction.id,
        date: transaction.createdAt?.formatted || '—',
        reference: transaction.reference || '—',
        // Sem referência não há detalhe a abrir: a linha continua estática em vez de levar a um 404.
        externalId: transaction.reference || '',
        type: transaction.operationLabel || '—',
        amount: montar(transaction.amount?.formatted),
        fee: transaction.fee ? montar(transaction.fee.formatted) : null,
        finalBalance: transaction.finalBalance ? montar(transaction.finalBalance.formatted) : null,
        status: {
          label: transaction.status?.label || '—',
          className: toneClass,
        },
      };
    });
  }, [transactions]);

  const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount]);
  const hasMoreRows = visibleCount < rows.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, rows.length));
  };

  // Clicar em qualquer ponto da linha abre o detalhe, mas quem responde pelo teclado e pelo leitor
  // de tela é o link da referência: uma linha focável repetiria cada movimentação na navegação por
  // Tab, e um role="link" na <tr> tiraria da tabela a estrutura de linhas e colunas.
  const abrirDetalhe = (evento, externalId) => {
    if (evento.target.closest('a')) return;
    navigate(`/transactions/${externalId}`);
  };

  const hasTransactions = rows.length > 0;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar
          active="transactions"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Extrato</h1>
              <p className="text-sm text-slate-500">Detalhes de todas as movimentações da sua conta.</p>
            </div>
            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
            <div className="mb-4 flex justify-end">
              <span className="text-xs text-slate-400">
                Atualizado em{' '}
                {new Intl.DateTimeFormat('pt-BR', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date())}
              </span>
            </div>

            {loading && <div className="text-sm text-gray-500">Carregando extrato...</div>}

            {error && !loading ? (
              <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
            ) : null}

            {!loading && !error && !hasTransactions ? (
              <div className="flex flex-col items-center justify-center text-center py-16 gap-4">
                <img src="/img/payment.webp" alt="" className="w-24 h-auto opacity-80" />
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Nenhuma transação encontrada</h2>
                  <p className="text-sm text-slate-500">Assim que você realizar movimentações, elas aparecerão aqui.</p>
                </div>
              </div>
            ) : null}

            {hasTransactions && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead className="text-[10px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="py-3 text-left font-semibold">Data</th>
                      <th className="py-3 text-left font-semibold">Referência</th>
                      <th className="py-3 text-left font-semibold">Tipo</th>
                      <th className="py-3 text-left font-semibold">Valor</th>
                      <th className="py-3 text-left font-semibold">Taxa</th>
                      <th className="py-3 text-left font-semibold">Balanço Final</th>
                      <th className="py-3 text-left font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600">
                    {visibleRows.map((row) => (
                      <tr
                        key={row.id}
                        className={`transition-colors text-[11px] md:text-xs ${
                          row.externalId
                            ? 'cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400'
                            : ''
                        }`}
                        {...(row.externalId
                          ? { onClick: (evento) => abrirDetalhe(evento, row.externalId) }
                          : {})}
                      >
                        <td className="py-3 pr-3 whitespace-nowrap">{row.date}</td>
                        <td className="py-3 pr-3">
                          {row.externalId ? (
                            // Âncora de verdade, e não só o clique na linha: é o que faz
                            // cmd/ctrl+clique abrir em outra aba e o link poder ser copiado.
                            <Link
                              to={`/transactions/${row.externalId}`}
                              className="underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
                            >
                              {row.reference}
                            </Link>
                          ) : (
                            row.reference
                          )}
                        </td>
                        <td className="py-3 pr-3 uppercase">{row.type}</td>
                        <td className="py-3 pr-3">
                          {row.amount.prefix}
                          <MaskedValue>{row.amount.value}</MaskedValue>
                          {row.amount.suffix}
                        </td>
                        <td className="py-3 pr-3">
                          {row.fee ? (
                            <>
                              {row.fee.prefix}
                              <MaskedValue>{row.fee.value}</MaskedValue>
                              {row.fee.suffix}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-3 pr-3">
                          {row.finalBalance ? (
                            <>
                              {row.finalBalance.prefix}
                              <MaskedValue>{row.finalBalance.value}</MaskedValue>
                              {row.finalBalance.suffix}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-3">
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-semibold ${row.status.className}`}>
                            {row.status.label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {hasMoreRows && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
                >
                  Carregar mais
                  <i className="bi-arrow-down-short text-lg"></i>
                </button>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default Transactions;

